import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';

/**
 * InfoFallbackShell — shared chrome for editorial fallback pages.
 *
 * Mirrors the layout introduced by NotFoundPage (info-pages.html V1) so
 * the 4xx surfaces (404, 403, 401) and similar status pages share one
 * implementation: a saturated full-bleed gradient backdrop with the
 * editorial numeral on the left and the kicker / title / body / actions
 * stack on the right.
 *
 * Tones map to the mockup palette intent:
 *   - sun   → 404 / 401  (warm, recoverable)
 *   - coral → 403         (denied, action required)
 *   - ocean → 5xx          (system error, calmer cool palette)
 *
 * No EditorialPageLayout — fallback pages are auth-surface flows per
 * editorial doc §6 (no rail, no topbar, work pre-login as well).
 */

export type InfoFallbackTone = 'sun' | 'coral' | 'ocean';

export interface InfoFallbackAction {
  label: string;
  /** Either a route to link to, or a click handler — exactly one. */
  to?: string;
  onClick?: () => void;
  /** Whether the action is the primary call-to-action. */
  primary?: boolean;
  /** Optional icon side — defaults to right-arrow on primary, none on secondary. */
  iconSide?: 'left' | 'right' | 'none';
}

export interface InfoFallbackShellProps {
  /** Tone keys the gradient + accent colors. */
  tone: InfoFallbackTone;
  /** Big editorial numeral on the left ("404", "403", "401", etc.). */
  statusCode: string;
  /** Mono micro-line under the numeral (e.g. "HTTP · Forbidden"). */
  subLabel: string;
  /** Top kicker line (e.g. "RBAC · Location scope"). */
  kicker: string;
  /** Tag pill next to the kicker (e.g. "missing · location.settle"). */
  tag: string;
  /** Title fragment A — rendered before the italic emphasis. */
  titleA: string;
  /** Italic emphasis fragment — visually accented in serif italic. */
  titleEm: string;
  /** Title fragment B — rendered after the italic emphasis. */
  titleB: string;
  /** Body paragraph. */
  body: ReactNode;
  /** Primary action — typically the "fix" or "next step" CTA. */
  primaryAction: InfoFallbackAction;
  /** Secondary action — typically a way out (Go home, Sign in, etc.). */
  secondaryAction?: InfoFallbackAction;
  /** Optional aria-labelledby target for the title element. */
  titleId?: string;
}

const TONE_GRADIENTS: Record<InfoFallbackTone, string> = {
  sun: 'linear-gradient(120deg, hsl(var(--sun-300)), hsl(var(--sand-500)) 50%, hsl(var(--sand-700)))',
  coral:
    'linear-gradient(120deg, hsl(var(--coral-300)), hsl(var(--sand-500)) 50%, hsl(var(--coral-700)))',
  ocean:
    'linear-gradient(120deg, hsl(var(--ocean-300)), hsl(var(--sand-500)) 50%, hsl(var(--ocean-700)))',
};

const TONE_RADIAL: Record<InfoFallbackTone, [string, string]> = {
  sun: ['hsl(var(--sun-500) / 0.30)', 'hsl(var(--coral-500) / 0.18)'],
  coral: ['hsl(var(--coral-500) / 0.30)', 'hsl(var(--sun-500) / 0.18)'],
  ocean: ['hsl(var(--ocean-500) / 0.30)', 'hsl(var(--sage-500) / 0.18)'],
};

const TONE_TAG_TEXT: Record<InfoFallbackTone, string> = {
  sun: 'text-sun-700',
  coral: 'text-coral-700',
  ocean: 'text-ocean-700',
};

function ActionButton({
  action,
  primary,
}: {
  action: InfoFallbackAction;
  primary: boolean;
}) {
  const baseClass = primary
    ? 'inline-flex items-center gap-2 rounded-lg border border-transparent bg-sand-700 px-4 py-2.5 text-sm font-semibold text-sand-50 shadow-sm transition-colors hover:bg-sand-900'
    : 'inline-flex items-center gap-2 rounded-lg border border-white/40 bg-white/90 px-4 py-2.5 text-sm font-semibold text-sand-900 backdrop-blur transition-colors hover:bg-white';
  const iconSide = action.iconSide ?? (primary ? 'left' : 'right');
  const Icon = iconSide === 'left' ? ArrowLeft : iconSide === 'right' ? ArrowRight : null;

  const content = (
    <>
      {Icon && iconSide === 'left' ? <Icon className="h-4 w-4" aria-hidden="true" /> : null}
      <span>{action.label}</span>
      {Icon && iconSide === 'right' ? <Icon className="h-4 w-4" aria-hidden="true" /> : null}
    </>
  );

  if (action.to) {
    return (
      <Link to={action.to} className={baseClass}>
        {content}
      </Link>
    );
  }
  return (
    <button type="button" onClick={action.onClick} className={baseClass}>
      {content}
    </button>
  );
}

export function InfoFallbackShell({
  tone,
  statusCode,
  subLabel,
  kicker,
  tag,
  titleA,
  titleEm,
  titleB,
  body,
  primaryAction,
  secondaryAction,
  titleId,
}: InfoFallbackShellProps) {
  return (
    <main
      role="main"
      aria-labelledby={titleId}
      className="relative isolate flex min-h-screen items-center overflow-hidden p-10 text-sand-50 lg:p-14"
      style={{ backgroundImage: TONE_GRADIENTS[tone] }}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: [
            `radial-gradient(circle at 18% 22%, ${TONE_RADIAL[tone][0]}, transparent 55%)`,
            `radial-gradient(circle at 82% 78%, ${TONE_RADIAL[tone][1]}, transparent 55%)`,
          ].join(', '),
        }}
      />

      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[40px]"
        style={{
          backgroundImage:
            'radial-gradient(50% 140% at 50% 100%, rgba(255,253,246,.95), transparent 70%)',
        }}
      />

      <div className="relative z-10 mx-auto grid w-full max-w-5xl gap-10 lg:grid-cols-[auto_1fr] lg:items-center lg:gap-14">
        {/* Editorial numeral */}
        <div className="relative shrink-0 text-center lg:text-left">
          <span
            aria-hidden="true"
            className="absolute inset-0 translate-x-1 translate-y-1 font-serif text-[140px] italic font-medium leading-[0.92] tracking-tight text-black/20 blur-[2px] sm:text-[180px]"
          >
            {statusCode}
          </span>
          <span className="relative block font-serif text-[140px] italic font-medium leading-[0.92] tracking-tight sm:text-[180px]">
            {statusCode}
          </span>
          <span className="mt-4 inline-block rounded border border-white/30 bg-white/15 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] backdrop-blur">
            {subLabel}
          </span>
        </div>

        {/* Copy + actions */}
        <div className="max-w-xl">
          <p className="mb-3 inline-flex items-center gap-3 font-mono text-[10.5px] font-semibold uppercase tracking-[0.18em] text-sand-50/90">
            <span>{kicker}</span>
            <span className={`rounded bg-white/90 px-2 py-0.5 text-[10px] tracking-[0.14em] ${TONE_TAG_TEXT[tone]}`}>
              {tag}
            </span>
          </p>
          <h1
            id={titleId}
            className="font-serif text-[32px] font-medium leading-tight tracking-tight text-sand-50 sm:text-[40px]"
          >
            {titleA}{' '}
            <em className="italic font-normal text-sand-200">{titleEm}</em>{' '}
            {titleB}
          </h1>
          <div className="mt-4 max-w-md text-sm leading-relaxed text-sand-50/90">{body}</div>

          <div className="mt-7 flex flex-wrap gap-3">
            <ActionButton action={primaryAction} primary />
            {secondaryAction ? <ActionButton action={secondaryAction} primary={false} /> : null}
          </div>
        </div>
      </div>
    </main>
  );
}
